import React, { useState, useEffect } from 'react';
import BREAKPOINTS, { BreakpointsType } from '../../../style/breakpoints';

const STRING = 'string';
const BOOLEAN = 'boolean';
const NUMBER = 'number';
const DIMENSIONREGEX = /[height|width]$/;
const LETTERREGEX = /[A-Z]/g;

const camelToHyphen = (str: string): string => str.replace(LETTERREGEX, (m) => `-${m.toLowerCase()}`).toLowerCase();

const objectToString = (
  query: { [key: string]: string | boolean | number | undefined },
  isStrictMedia: boolean,
): string =>
  Object.keys(query)
    .map((feature) => {
      let value = query[feature as keyof object];
      const valueType = typeof value;
      feature = camelToHyphen(feature);
      // if value is a string and exists in our breakpoints obj then populate
      // the variable with its px value
      if (valueType === STRING && BREAKPOINTS[value as keyof BreakpointsType]) {
        const breakPointValue = BREAKPOINTS[value as keyof BreakpointsType] as string;
        const isMax = feature.includes('max-width');

        value = isStrictMedia && isMax ? `${parseInt(breakPointValue) - 1}px` : breakPointValue;
      }
      if (valueType === BOOLEAN) {
        return value ? feature : `not ${feature}`;
      }
      if (valueType === NUMBER && DIMENSIONREGEX.test(feature)) {
        value = `${value}px`;
      }

      return `(${feature}: ${value})`;
    })
    .join(' and ');

const createUseMedia =
  (effect: (effect: React.EffectCallback, deps?: React.DependencyList) => void) =>
  (rawQuery: { [key: string]: string | boolean | number | undefined }, defaultState?: boolean): boolean => {
    const [state, setState] = useState(defaultState);
    const isStrictMedia: boolean =
      Object.prototype.hasOwnProperty.call(rawQuery, `maxWidth`) &&
      Object.prototype.hasOwnProperty.call(rawQuery, `minWidth`);
    const query = objectToString(rawQuery, isStrictMedia);
    effect(() => {
      if (typeof window !== 'undefined') {
        let mounted = true;
        const mql = window && window.matchMedia && window.matchMedia(query);

        if (mql) {
          const onChange = () => {
            if (!mounted) return;
            setState(!!mql.matches);
          };

          mql.addListener(onChange);
          setState(mql.matches);

          return () => {
            mounted = false;
            mql.removeListener(onChange);
          };
        } else {
          return;
        }
      } else {
        return;
      }
    }, [query]);

    if (typeof state === 'undefined' && typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia(query);
      if (mql) {
        setState(mql.matches);
      }
    }
    return !!state;
  };

export const useMedia = createUseMedia(useEffect);

export default useMedia;
